import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Checkbox } from "../ui/checkbox";
import ReportView from "./ReportView";
import { Play, Save, X, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const err = (e) => e?.response?.data?.detail || e.message;
const slug = (o) => String(o).replace(/[^a-z0-9]/gi, "-").toLowerCase();

/** Multi-select filter dropdown — pick any number of values (empty = all). */
export function MultiFilter({ f, values, onChange, testPrefix = "builder-filter" }) {
  const sel = values || [];
  const label = sel.length === 0 ? "all" : sel.length === 1 ? sel[0] : `${sel.length} selected`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={`builder-filter-${f.id}`}
          className={`inline-flex items-center gap-1.5 rounded-sm border h-8 px-3 text-xs bg-white transition-colors ${
            sel.length ? "border-[#00579e] text-[#00579e] font-medium" : "border-border text-foreground"
          }`}
        >
          {f.label}: {label}
          <ChevronDown className="w-3.5 h-3.5 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-1.5 max-h-72 overflow-y-auto" align="start">
        <button
          data-testid={`builder-filter-${f.id}-clear`}
          onClick={() => onChange([])}
          className={`w-full text-left text-xs px-2 py-1.5 rounded-sm hover:bg-[hsl(var(--cs-tint))] ${sel.length === 0 ? "font-semibold text-[#00579e]" : ""}`}
        >
          All {f.label.toLowerCase()}s
        </button>
        {f.options.map((o) => (
          <label
            key={o}
            className="flex items-center gap-2 text-xs px-2 py-1.5 rounded-sm hover:bg-[hsl(var(--cs-tint))] cursor-pointer"
          >
            <Checkbox
              checked={sel.includes(o)}
              data-testid={`builder-filter-${f.id}-${slug(o)}`}
              onCheckedChange={(c) => onChange(c ? [...sel, o] : sel.filter((x) => x !== o))}
            />
            <span className="truncate">{o}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/** Field-mapping custom report builder. `api` = { fields, preview, save } · used by MSP and portal. */
export default function CustomReportBuilder({ api, onSaved, accent = "#00579e" }) {
  const [fields, setFields] = useState(null);
  const [name, setName] = useState("");
  const [dims, setDims] = useState(["service", "month"]);
  const [measures, setMeasures] = useState([]);
  const [monthsN, setMonthsN] = useState("3");
  const [filters, setFilters] = useState({});
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api.fields().then((f) => {
      setFields(f);
      setMeasures(f.measures.slice(0, 2).map((m) => m.id));
    }).catch(console.error);
    // eslint-disable-next-line
  }, []);

  if (!fields) return <div className="text-sm text-muted-foreground py-6">Loading builder…</div>;

  const toggleDim = (id) => {
    setDims((d) => d.includes(id) ? d.filter((x) => x !== id)
      : d.length >= fields.max_dimensions ? (toast.error(`Max ${fields.max_dimensions} dimensions`), d) : [...d, id]);
  };
  const toggleMeasure = (id) =>
    setMeasures((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));

  const definition = () => ({
    group_by: dims,
    measures,
    filters: Object.fromEntries(Object.entries(filters).filter(([, v]) => Array.isArray(v) && v.length)),
    last_n_months: parseInt(monthsN, 10),
  });

  const run = async () => {
    if (!dims.length) { toast.error("Pick at least one dimension"); return; }
    if (!measures.length) { toast.error("Pick at least one measure"); return; }
    setRunning(true);
    try {
      setPreview(await api.preview({ name: name || "Custom report", ...definition() }));
    } catch (e) { toast.error("Preview failed", { description: err(e) }); }
    finally { setRunning(false); }
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Give the report a name first"); return; }
    try {
      const doc = await api.save(name.trim(), definition());
      toast.success(`"${doc.name}" saved — it now appears under Custom Reports`);
      onSaved?.(doc);
    } catch (e) { toast.error("Save failed", { description: err(e) }); }
  };

  const chip = (active) =>
    `px-2.5 py-1 rounded-full text-[11px] border transition-colors ${active ? "text-white border-transparent" : "bg-white border-border hover:bg-[hsl(var(--cs-tint))]"}`;

  return (
    <div className="space-y-4" data-testid="custom-report-builder">
      <div className="cs-card p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Report name (e.g. Monthly spend by provider & service)"
            data-testid="builder-name-input" className="rounded-sm h-9 text-xs max-w-md" />
          <div className="ml-auto flex items-center gap-2">
            <Select value={monthsN} onValueChange={setMonthsN}>
              <SelectTrigger className="rounded-sm h-9 text-xs w-[100px]" data-testid="builder-months-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["1", "3", "6", "12"].map((m) => <SelectItem key={m} value={m}>{m} mo</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={run} disabled={running} data-testid="builder-run-button"
              className="rounded-full h-9 text-xs text-white" style={{ backgroundColor: accent }}>
              <Play className="w-3.5 h-3.5 mr-1" /> {running ? "Running…" : "Run preview"}
            </Button>
            <Button variant="outline" onClick={save} data-testid="builder-save-button" className="rounded-full h-9 text-xs">
              <Save className="w-3.5 h-3.5 mr-1" /> Save report
            </Button>
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5">
            Dimensions (group rows by — ordered, up to {fields.max_dimensions})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {fields.dimensions.map((d) => {
              const idx = dims.indexOf(d.id);
              return (
                <button key={d.id} onClick={() => toggleDim(d.id)} data-testid={`builder-dim-${d.id}`}
                  className={chip(idx >= 0)} style={idx >= 0 ? { backgroundColor: accent } : {}}>
                  {idx >= 0 && <span className="cs-mono mr-1 opacity-80">{idx + 1}</span>}{d.label}
                  {idx >= 0 && <X className="w-3 h-3 inline ml-1 opacity-70" />}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5">Measures (values to aggregate)</div>
          <div className="flex flex-wrap gap-1.5">
            {fields.measures.map((m) => (
              <button key={m.id} onClick={() => toggleMeasure(m.id)} data-testid={`builder-measure-${m.id}`}
                className={chip(measures.includes(m.id))} style={measures.includes(m.id) ? { backgroundColor: "#15803d" } : {}}>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Filters</span>
          {(fields.filterable || []).map((f) => (
            <MultiFilter
              key={f.id}
              f={f}
              values={filters[f.id]}
              onChange={(vals) => setFilters((prev) => ({ ...prev, [f.id]: vals }))}
            />
          ))}
          {Object.values(filters).some((v) => v?.length) && (
            <button
              data-testid="builder-filters-reset"
              onClick={() => setFilters({})}
              className="text-[11px] text-[#be185d] hover:underline inline-flex items-center gap-0.5"
            >
              <X className="w-3 h-3" /> clear filters
            </button>
          )}
        </div>
      </div>

      {preview
        ? <ReportView data={preview} />
        : <div className="cs-card p-10 text-center text-sm text-muted-foreground" data-testid="builder-empty">
            Map your fields above and hit <span className="font-semibold">Run preview</span> — the grouped result renders here with chart, totals and export.
          </div>}
    </div>
  );
}
